<template>
  <div
    ref="modalEl"
    class="modal fade"
    tabindex="-1"
    aria-labelledby="slipDetailsTitle"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="slipDetailsTitle">
            Lieferschein {{ slip?.slip_number ?? '' }}
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Schließen"></button>
        </div>

        <div v-if="slip" class="modal-body">
          <p class="slip-meta">
            {{ slip.servicepartner.description }} · {{ formatDate(slip.creation_timestamp) }}
            <span v-if="slip.created_by"> · {{ slip.created_by }}</span>
          </p>

          <div v-if="loading" class="spinner" aria-label="Lädt"></div>
          <div v-else-if="loadError" class="error">{{ loadError }}</div>
          <div v-else-if="devices.length === 0" class="error">
            Auf diesem Lieferschein steht kein Gerät mehr.
          </div>

          <div v-else class="table-responsive slip-table-wrap">
            <table class="table table-sm table-striped align-middle">
              <thead>
                <tr>
                  <th scope="col">R2-Nr</th>
                  <th scope="col">Gerät</th>
                  <th scope="col">IMEI / SN</th>
                  <th scope="col">Fehlerbild</th>
                  <th scope="col" class="slip-price-cell">Preis</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="d in devices" :key="d.stock_device_id">
                  <td class="ticket-mono">{{ d.stock_number }}</td>
                  <td>{{ d.description }}</td>
                  <td class="ticket-mono">{{ d.imei_sn || '—' }}</td>
                  <td>{{ d.error_pattern || '—' }}</td>
                  <td class="slip-price-cell">{{ d.error_pattern_price_label || '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="pdfError" class="error pdf-error">{{ pdfError }}</div>
        </div>

        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-outline-secondary"
            :disabled="pdfBusy"
            @click="onDownloadPdf"
          >{{ pdfBusy ? 'Lädt …' : 'PDF herunterladen' }}</button>
          <button type="button" class="btn btn-primary" data-bs-dismiss="modal">Schließen</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue';
import { Modal } from 'bootstrap';
import {
  downloadPdf,
  listSlipDevices,
  type DeliverySlip,
  type DeliverySlipCandidate,
} from '../services/deliverySlips';

/**
 * Die Geräte eines bereits erstellten Lieferscheins, gelesen aus den
 * Lieferscheinzeilen — also genau das, was auch auf dem Papier steht.
 * Aufruf via `open(slip)`.
 */
const modalEl = useTemplateRef<HTMLDivElement>('modalEl');
let   modal: Modal | null = null;

const slip      = ref<DeliverySlip | null>(null);
const devices   = ref<DeliverySlipCandidate[]>([]);
const loading   = ref(false);
const loadError = ref<string | null>(null);
const pdfBusy   = ref(false);
const pdfError  = ref<string | null>(null);

onMounted(() => {
  if (modalEl.value) {
    modal = new Modal(modalEl.value);
  }
});

onBeforeUnmount(() => {
  modal?.dispose();
  modal = null;
});

function open(item: DeliverySlip): void {
  slip.value      = item;
  devices.value   = [];
  loading.value   = true;
  loadError.value = null;
  pdfBusy.value   = false;
  pdfError.value  = null;
  modal?.show();
  void load();
}

async function load(): Promise<void> {
  if (slip.value === null) return;
  try {
    devices.value = await listSlipDevices(slip.value.id);
  } catch (e) {
    loadError.value = e instanceof Error ? e.message : 'Die Geräte konnten nicht geladen werden.';
  } finally {
    loading.value = false;
  }
}

async function onDownloadPdf(): Promise<void> {
  if (slip.value === null || pdfBusy.value) return;
  pdfBusy.value  = true;
  pdfError.value = null;
  try {
    await downloadPdf(slip.value);
  } catch (e) {
    pdfError.value = e instanceof Error ? e.message : 'Das PDF konnte nicht geladen werden.';
  } finally {
    pdfBusy.value = false;
  }
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat('de-AT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(d);
}

defineExpose({ open });
</script>

<style scoped>
.slip-meta {
  margin-bottom: 0.75rem;
  color: #6c757d;
}

.slip-table-wrap {
  max-height: 55vh;
  overflow-y: auto;
}

.slip-price-cell {
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.spinner {
  width: 1.5rem;
  height: 1.5rem;
  border: 2px solid #ced4da;
  border-top-color: #0d6efd;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.pdf-error {
  margin-top: 0.75rem;
}

.error {
  color: #dc3545;
}

.ticket-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}
</style>
